import React, { useState, useEffect } from 'react';
import { PageHeader } from '@/components/common/PageHeader';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Coins, Gift, Sparkles } from 'lucide-react';
import API from '@/api/client';
import { formatNumber } from '@/utils/helpers';
import { toast } from 'sonner';

const RewardsPage = () => {
  const [loading, setLoading] = useState(true);
  const [pointsData, setPointsData] = useState(null);
  const [rewards, setRewards] = useState([]);
  const [redeeming, setRedeeming] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [pointsRes, rewardsRes] = await Promise.all([
        API.get('/customer/points'),
        API.get('/customer/rewards/catalog'),
      ]);
      setPointsData(pointsRes.data);
      setRewards(rewardsRes.data.rewards);
    } catch (error) {
      console.error('Error fetching rewards:', error);
      toast.error('Failed to load rewards');
    } finally {
      setLoading(false);
    }
  };

  const handleRedeem = async (rewardId, pointsCost) => {
    if (pointsData.wallet.balance < pointsCost) {
      toast.error('Insufficient points');
      return;
    }

    try {
      setRedeeming(rewardId);
      const response = await API.post(`/customer/points/redeem?reward_id=${rewardId}`);
      toast.success(`Redeemed! Code: ${response.data.redemption_code}`);
      fetchData(); // Refresh data
    } catch (error) {
      console.error('Error redeeming reward:', error);
      toast.error('Failed to redeem reward');
    } finally {
      setRedeeming(null);
    }
  };

  if (loading) {
    return <LoadingSpinner size="lg" text="Loading rewards..." />;
  }

  const currentBalance = pointsData?.wallet?.balance || 0;
  const currentTier = pointsData?.wallet?.tier || 'bronze';

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-50 px-4 md:px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img
              src="https://customer-assets.emergentagent.com/job_e65b2a0c-b7a8-46e9-8a92-d5df4207b5ae/artifacts/8vn39jol_Physiomobile%20Logo.jpeg"
              alt="Physiomobile Logo"
              className="h-10"
            />
            <h1 className="font-heading text-xl font-bold text-gray-800">Rewards Catalog</h1>
          </div>
          <Button onClick={() => window.location.href = '/customer/dashboard'} variant="outline">
            Back to Dashboard
          </Button>
        </div>
      </header>

      <main className="px-4 md:px-6 lg:px-8 py-8">
        <PageHeader title="Redeem Your Points" subtitle="Choose from our rewards catalog" />

        {/* Points Balance Card */}
        <Card className="p-6 mb-8 bg-gradient-to-br from-primary/5 to-primary/10" data-testid="points-balance-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Your Balance</p>
              <div className="flex items-baseline gap-2">
                <span className="font-heading text-5xl font-bold text-primary">
                  {formatNumber(currentBalance)}
                </span>
                <span className="text-xl text-muted-foreground">points</span>
              </div>
              <Badge className="mt-2">{currentTier.toUpperCase()} TIER</Badge>
            </div>
            <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center">
              <Sparkles className="w-10 h-10 text-primary" />
            </div>
          </div>
        </Card>

        {/* Rewards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {rewards.map((reward) => (
            <Card
              key={reward.id}
              className="p-6 hover:shadow-lg transition-all"
              data-testid={`reward-card-${reward.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Gift className="w-6 h-6 text-primary" />
                </div>
                <div className="flex items-center gap-2">
                  <Coins className="w-4 h-4 text-primary" />
                  <span className="font-heading text-lg font-bold text-primary">
                    {formatNumber(reward.points_cost)}
                  </span>
                </div>
              </div>

              <h3 className="font-heading text-xl font-semibold mb-2 text-gray-800">
                {reward.name}
              </h3>
              <p className="text-sm text-muted-foreground mb-4">{reward.description}</p>

              {reward.reward_type === 'discount' && reward.value > 0 && (
                <div className="text-sm font-medium text-green-600 mb-4">
                  Worth RM {reward.value}
                </div>
              )}

              <Button
                onClick={() => handleRedeem(reward.id, reward.points_cost)}
                disabled={currentBalance < reward.points_cost || redeeming === reward.id}
                className="w-full"
                data-testid={`redeem-button-${reward.id}`}
              >
                {redeeming === reward.id
                  ? 'Redeeming...'
                  : currentBalance < reward.points_cost
                  ? 'Insufficient Points'
                  : 'Redeem Now'}
              </Button>
            </Card>
          ))}
        </div>
      </main>
    </div>
  );
};

export default RewardsPage;